import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { ArrowLeft, Play, CheckCircle, ArrowRight } from "lucide-react";
import { useLang } from "../LangContext";
import Breadcrumb from "../Breadcrumb";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_prodimmo-media/artifacts/rb17en5i_logo_prodimmo-removebg-preview.png";

export default function VideoImmobilier() {
  const { t } = useLang();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const features = [
    "Vidéo cinématique 4K de votre bien immobilier",
    "Prises de vue intérieures et extérieures",
    "Montage professionnel avec étalonnage couleur",
    "Musique libre de droits incluse",
    "Formats optimisés pour portails immobiliers",
    "Livraison rapide sous 5 jours ouvrés",
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Vidéo Immobilière Professionnelle | Prodimmo , Suisse</title>
        <meta name="description" content="Production vidéo immobilière professionnelle en Suisse romande. Vidéos cinématiques 4K avec drone pour valoriser vos biens et accélérer vos ventes." />
        <meta property="og:title" content="Vidéo Immobilière Professionnelle | Prodimmo , Suisse" />
        <meta property="og:description" content="Les annonces avec vidéo reçoivent 403% de demandes en plus. Vidéo cinématique 4K pour vos biens immobiliers." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://www.prodimmo.ch/video-immobilier" />
      </Helmet>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Prodimmo" className="h-9" />
            <span className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit' }}>Prodimmo</span>
          </Link>
          <Link to="/" className="flex items-center gap-2 text-sm text-slate-600 hover:text-[#73B5E2] transition-colors">
            <ArrowLeft className="w-4 h-4" /> Retour
          </Link>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumb items={[
            { label: "Prestations", href: "/#prestations" },
            { label: "Vidéo Immobilière" },
          ]} />
        </div>
      </nav>

      <section className="pt-32 pb-20 bg-gradient-to-b from-[#F8FAFC] to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="text-[#73B5E2] font-medium text-sm uppercase tracking-widest">Vidéo immobilière</span>
            <h1 className="mt-3 text-4xl sm:text-5xl font-bold text-slate-900 leading-tight" style={{ fontFamily: 'Outfit' }}>
              Vidéo immobilier<br />
              <span className="text-[#73B5E2]">pour vendre vos biens</span>
            </h1>
            <p className="mt-6 text-slate-600 text-lg max-w-2xl leading-relaxed">
              La vidéo immobilière est devenue indispensable pour se démarquer. Nos vidéos cinématiques captivent les acheteurs 
              potentiels et génèrent plus de visites que les annonces avec photos seules. Basés à Bulle, nous intervenons 
              dans toute la Suisse romande.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}>
            <div className="relative rounded-2xl overflow-hidden aspect-video bg-[#0F172A]">
              <video autoPlay muted loop playsInline className="w-full h-full object-cover">
                <source src="/payerne.mp4" type="video/mp4" />
              </video>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  <Play className="w-6 h-6 text-white ml-1" fill="white" />
                </div>
              </div>
            </div>
            <p className="text-sm text-slate-500 mt-3 text-center">Exemple de vidéo immobilière , Patrick Immobilier, Payerne</p>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }}>
            <h2 className="text-2xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Outfit' }}>
              Ce que comprend notre service vidéo
            </h2>
            <ul className="space-y-4">
              {features.map((f, i) => (
                <motion.li key={i} initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 + i * 0.08 }} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#73B5E2] mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700">{f}</span>
                </motion.li>
              ))}
            </ul>
            <div className="mt-8 flex gap-4">
              <Link to="/#contact" className="bg-[#73B5E2] text-white hover:bg-[#5AA0D0] rounded-full px-6 py-3 text-sm font-medium transition-all hover:scale-105 shadow-lg shadow-blue-500/20">
                Demander un devis
              </Link>
              <Link to="/#tarifs" className="border border-slate-200 text-slate-700 hover:border-[#73B5E2] rounded-full px-6 py-3 text-sm font-medium transition-all">
                Voir les tarifs
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-16 bg-[#F8FAFC]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit' }}>
            Pourquoi la vidéo immobilière fait la différence
          </h2>
          <p className="text-slate-600 leading-relaxed mb-8">
            Les annonces immobilières avec vidéo reçoivent en moyenne 403% de demandes de plus que celles sans. 
            Une vidéo permet aux acheteurs de se projeter dans le bien avant même la visite, réduisant les visites 
            non qualifiées et accélérant les ventes.
          </p>
          <div className="grid grid-cols-3 gap-6">
            {[{ val: "403%", desc: "Plus de demandes" }, { val: "2x", desc: "Plus de temps sur l'annonce" }, { val: "85%", desc: "Des acheteurs préfèrent la vidéo" }].map((s, i) => (
              <div key={i}>
                <p className="text-3xl font-bold text-[#73B5E2]" style={{ fontFamily: 'Outfit' }}>{s.val}</p>
                <p className="text-sm text-slate-500 mt-1">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vimeo video example */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-4 text-center" style={{ fontFamily: 'Outfit' }}>
            Exemple de réalisation vidéo
          </h2>
          <p className="text-slate-500 text-center mb-8">Vidéo cinématique réalisée pour un bien immobilier en Suisse romande</p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="rounded-2xl overflow-hidden shadow-lg bg-slate-900 aspect-video">
            <iframe
              src="https://player.vimeo.com/video/1175940458?autoplay=0&title=0&byline=0&portrait=0"
              className="w-full h-full"
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
              title="Vidéo immobilière Prodimmo"
              data-testid="video-vimeo-embed"
            />
          </motion.div>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-center gap-4">
          <Link to="/photo-immobilier" className="flex items-center gap-2 text-[#73B5E2] hover:text-[#5AA0D0] font-medium text-sm transition-colors">
            Photo immobilière <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/drone-fpv-immobilier" className="flex items-center gap-2 text-[#73B5E2] hover:text-[#5AA0D0] font-medium text-sm transition-colors">
            Drone FPV <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
